import { GeckoColorScale } from 'color-scale-kit'
import {
  CategoricalSummaryStatistics,
  useCategoricalDataSummaryStatsFetcher,
} from 'features/dataLayers'
import { useGeckoLocale } from 'features/locale'
import isNil from 'lodash/isNil'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { styled } from 'styled-components'
import {
  DataType,
  GeckoComponentDataFrame,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  GeckoView,
} from 'types'

import {
  Area,
  CategoryInputs,
  MainCategoryData,
  SubCategoryData,
} from './CategoryInputs'
import {
  ValueBreakdownDisplayType,
  ValueBreakdownDisplayTypeSelector,
} from './ValueBreakdownDisplayTypeSelector'

interface CategorySelectorProps {
  selectedCDF: GeckoComponentDataFrame
  selectedColorScale: GeckoColorScale
  onFilteredValueRangeChange: (range: GeckoMeasurementRange | undefined) => void
  onCategoricalValuesSelected: (values: number[] | undefined) => void
}
export const CategorySelector: FC<CategorySelectorProps> = (props) => {
  const fetchSummaryStats = useCategoricalDataSummaryStatsFetcher()
  const [summaryStats, setSummaryStats] = React.useState<
    CategoricalSummaryStatistics | undefined
  >(undefined)
  useEffect(() => {
    fetchSummaryStats(props.selectedCDF).then((data) => {
      setSummaryStats(data)
    })
  }, [fetchSummaryStats, props.selectedCDF])

  const { locale, getPreferredMeasurementUnit } = useGeckoLocale()

  const totalCoveredArea: Area | undefined = useMemo(() => {
    if (!summaryStats) return
    const binSize = props.selectedCDF.data.binning.bin_size
    // Calculating area in polar coordinates is not currently supported
    if (binSize.width.unit === GeckoMeasurementUnit.Degrees) return

    const xPreferredUnit = getPreferredMeasurementUnit(
      GeckoMeasurementUnit.Feet
    )
    const yPreferredUnit = getPreferredMeasurementUnit(
      GeckoMeasurementUnit.Feet
    )

    const area =
      binSize.width.toNumber(xPreferredUnit) *
      binSize.height.toNumber(yPreferredUnit)

    return {
      value: area * summaryStats.num_values,
      xUnit: xPreferredUnit,
      yUnit: yPreferredUnit,
    }
  }, [
    getPreferredMeasurementUnit,
    props.selectedCDF.data.binning.bin_size,
    summaryStats,
  ])

  const mainCategoryBreakdown: MainCategoryData[] | undefined = useMemo(() => {
    if (!summaryStats) return
    const measurmentUnit = props.selectedCDF.data.params.measurement_unit
    if (measurmentUnit.dataType !== DataType.Categorical)
      throw new Error('Invalid measurement unit')

    const allAcceptableValues = Array.from(
      {
        length:
          measurmentUnit.acceptableValueRange[1] -
          measurmentUnit.acceptableValueRange[0] +
          1, // +1 because acceptableValueRange is inclusive
      },
      (_, i) => i + measurmentUnit.acceptableValueRange[0]
    )

    return allAcceptableValues.map((value) => {
      const percent =
        summaryStats.percentages[value] ??
        GeckoMeasurement.Zero(GeckoMeasurementUnit.Percent)

      const area = isNil(totalCoveredArea)
        ? undefined
        : getSubArea(totalCoveredArea, percent)

      return {
        id: `${value}`,
        value,
        percent,
        displayString: measurmentUnit.formatNumber({
          num: value,
          variant: 'narrow',
          locale,
        }),
        area,
      }
    })
  }, [
    locale,
    props.selectedCDF.data.params.measurement_unit,
    summaryStats,
    totalCoveredArea,
  ])

  const subCategoryBreakdown: SubCategoryData[] = useMemo(() => {
    if (!summaryStats) return []
    const view = props.selectedCDF.data.params.view
    const unit = props.selectedCDF.data.params.measurement_unit
    const subCategories = getSubCategories(view, unit)
    if (!subCategories.length) return []
    return subCategories.map(([name, values, unselectable]) => {
      const totalPercent = values.reduce((acc, value) => {
        return acc.plus(
          summaryStats.percentages[value] ??
            GeckoMeasurement.Zero(GeckoMeasurementUnit.Percent)
        )
      }, GeckoMeasurement.Zero(GeckoMeasurementUnit.Percent))

      const area = isNil(totalCoveredArea)
        ? undefined
        : getSubArea(totalCoveredArea, totalPercent)

      return {
        displayString: name,
        percent: totalPercent,
        area,
        values,
        id: `${values.join(',')}`,
        unselectable,
      }
    })
  }, [
    props.selectedCDF.data.params.measurement_unit,
    props.selectedCDF.data.params.view,
    summaryStats,
    totalCoveredArea,
  ])

  // ----------------------------------------------------------------------------
  // Selection

  const [selectedIds, setSelectedIds] = useState<string[]>([])

  // Auto-select all categories initially
  useEffect(() => {
    if (mainCategoryBreakdown) {
      const allCategoryIds = mainCategoryBreakdown.map((cat) => cat.id)
      setSelectedIds(allCategoryIds)
    }
  }, [mainCategoryBreakdown])

  const handleIdSelection = useCallback(
    (newId: string) => {
      const newlySelectedId = mainCategoryBreakdown?.find(
        ({ id }) => id === newId
      )?.id

      if (isNil(newlySelectedId)) {
        console.warn(`Could not find category with id ${newId}`)
        return
      }

      setSelectedIds((prevSelectedIds) => {
        const isCurrentlySelected = prevSelectedIds.includes(newlySelectedId)
        if (isCurrentlySelected) {
          return prevSelectedIds.filter((id) => id !== newlySelectedId)
        } else {
          return [...prevSelectedIds, newlySelectedId]
        }
      })
    },
    [mainCategoryBreakdown]
  )

  // Handle filtering for categorical views (multi-selection)
  const { onFilteredValueRangeChange, onCategoricalValuesSelected } = props
  useEffect(() => {
    if (selectedIds.length === 0) {
      onFilteredValueRangeChange(
        GeckoMeasurementRange.CreateFromValues(
          -1,
          -1,
          props.selectedCDF.data.params.measurement_unit
        )
      )
      onCategoricalValuesSelected(undefined)
      return
    }

    const selectedMainCategories =
      mainCategoryBreakdown?.filter(({ id }) => selectedIds.includes(id)) ?? []

    const allSelectedValues = selectedMainCategories.map(
      (category) => category.value
    )

    if (allSelectedValues.length === 0) {
      onFilteredValueRangeChange(undefined)
      return
    }

    onCategoricalValuesSelected(allSelectedValues)
    onFilteredValueRangeChange(undefined)
  }, [
    mainCategoryBreakdown,
    props.selectedCDF.data.params.measurement_unit,
    selectedIds,
    onFilteredValueRangeChange,
    onCategoricalValuesSelected,
  ])

  // ----------------------------------------------------------------------------
  // Value Display Type

  const [valueDisplayType, setValueDisplayType] =
    useState<ValueBreakdownDisplayType>(ValueBreakdownDisplayType.Percent)

  return !mainCategoryBreakdown ? null : (
    <Container data-testid='controlpanel-category-selector'>
      <CategoryInputs
        mainCategoryBreakdown={mainCategoryBreakdown}
        subCategoryBreakdown={subCategoryBreakdown ?? []}
        selectedColorScale={props.selectedColorScale}
        selectedIds={selectedIds}
        setSelectedId={handleIdSelection}
        valueToDisplay={valueDisplayType}
      />
      {!totalCoveredArea ? null : (
        <ValueBreakdownDisplayTypeSelector
          canDisplayArea={!isNil(totalCoveredArea)}
          valueDisplayType={valueDisplayType}
          setValueDisplayType={setValueDisplayType}
        />
      )}
    </Container>
  )
}

// ----------------------------------------------------------------------------
// Utilities

const Container = styled.div`
  margin-bottom: 0.5rem;
`

/**
 * Creates a subcategory configuration with clear intent
 */
function createSubCategory(
  name: string,
  values: number[],
  isUnselectable: boolean
): [string, number[], boolean] {
  return [name, values, isUnselectable]
}

/**
 * A function that, for a given view and measurement unit, gets any sub-categories.
 *
 * An example of this is for the Emat-Category view + the Relation To Nominal view,
 * where the sub-category is the "Growth Work Upper Bound", or number of bad readings
 * and number of invalidated readings.
 *
 * @return a list, where each value has the name at index 0, the values that comprise it
 *         at index 1, and whether it's unselectable at index 2.
 */
function getSubCategories(
  view: GeckoView,
  unit: GeckoMeasurementUnit
): [string, number[], boolean][] {
  if (
    view.id === GeckoView.EMATCategory.id &&
    unit.id === GeckoMeasurementUnit.RelationToNominal.id
  ) {
    // Growth Work Upper Bound combines categories 1 and 2 but is not selectable
    return [createSubCategory('Growth Work Upper Bound', [1, 2], true)]
  }
  return []
}

function getSubArea(totalArea: Area, percent: GeckoMeasurement) {
  return {
    value: totalArea.value * (percent.num / 100),
    xUnit: totalArea.xUnit,
    yUnit: totalArea.yUnit,
  }
}
